/**
 * /earthworks/invoices/:id — one EWI- invoice (fieldiq-api#1300, EW-6).
 * Opened from Finance → Invoices (the ONE invoice list), a job's Invoices tab,
 * the Ready to invoice queue, or a quote's Convert. The back arrow returns to
 * the list it came from, else to Finance → Invoices filtered to Earthworks.
 *
 * EVERY FIGURE IS THE API'S, AS STORED AT GENERATION. The money runs in the
 * API's order: work → holdback / retainage withheld → subtotal (the tax base)
 * → each levied tax → deposit credited → total → paid → balance. Nothing here
 * adds a line up or computes a tax. An absent figure reads `—`.
 *
 * ACTIONS (band, top right; admin and office staff — everyone else reads):
 *   draft   Edit details · Mark sent · Void
 *   sent    Record payment (while a balance is owing) · Void
 *   paid / void  none. Final.
 * Each opens INLINE under the band. Pay now (Batch 6b) is the Payment link
 * panel above Payments, on a sent invoice: the customer's no-login link,
 * where they pay by card when the tenant's Stripe is connected. QuickBooks
 * (S10) is the panel after it, on a sent or paid invoice: Push to QuickBooks,
 * or the QuickBooks id once it is there.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Ban, CreditCard, Pencil, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useEwMeta } from '../../../hooks/useEarthworks';
import { useEwInvoice } from '../../../hooks/useEwInvoices';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { ewDay, ewInstant, fmtMoney, fmtNumber, fmtPct } from '../../../lib/earthworks';
import {
  canWriteEwInvoices,
  ewInvoiceAdjustments,
  ewInvoiceCanMarkSent,
  ewInvoiceCanTakePayment,
  ewInvoiceCanVoid,
  ewInvoiceIsDraft,
  ewInvoiceKindText,
  ewInvoiceLineGroups,
  ewInvoiceStatusSkin,
  ewLeviedTaxRows,
  ewPaymentMethodText,
} from '../../../lib/ew-invoices';
import type { EwInvoice } from '../../../types/ew-invoices';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { EW_EYEBROW, EW_PRIMARY, EwBadge, EwEmpty, EwLoading, EwPanel, EwSpec, isNotFound } from '../EwBits';
import { EwInvoicePayLinkPanel, ewInvoiceShowsPayLink } from './EwInvoicePayLinkPanel';
import { EwInvoiceQboPanel, ewInvoiceShowsQbo } from './EwInvoiceQboPanel';
import { EwInvoiceEditPanel, EwInvoiceMarkSentPanel, EwInvoicePaymentPanel, EwInvoiceVoidPanel } from './EwInvoiceActions';

export const EW_FINANCE_INVOICES_PATH = '/finance/invoices';

const TH = 'px-4 py-2 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
const TD_MONEY = 'whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white';

function MoneyRow({ label, value, testId, sub, strong = false }: { label: string; value: string; testId: string; sub?: string | null; strong?: boolean }) {
  return (
    <tr className="border-l-4 border-l-transparent" data-testid={testId}>
      <th scope="row" colSpan={3} className={strong ? TH : 'px-4 py-2 text-right text-sm font-semibold text-fieldiq-text'}>
        {label}
        {sub && <span className="block text-xs font-normal normal-case tracking-normal text-fieldiq-muted">{sub}</span>}
      </th>
      <td className={`${TD_MONEY} ${strong ? 'text-base font-extrabold' : ''}`} data-testid={`${testId}-amount`}>{value}</td>
    </tr>
  );
}

function Totals({ inv }: { inv: EwInvoice }) {
  const cur = inv.currency;
  const { holdback, deposits } = ewInvoiceAdjustments(inv);
  const taxRows = ewLeviedTaxRows(inv);
  const hbLabel = inv.holdbackLabel || 'Holdback';
  return (
    <tfoot>
      <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent" data-testid="ew-invoice-work">
        <th scope="row" colSpan={3} className={TH}>Work</th>
        <td className={TD_MONEY} data-testid="ew-invoice-work-amount">{fmtMoney(inv.workSubtotal, cur)}</td>
      </tr>
      {holdback.map((l) => (
        <MoneyRow key={l.id} label={`${hbLabel} withheld`} sub={l.description} value={fmtMoney(l.amount, cur)} testId="ew-invoice-holdback" />
      ))}
      <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
        <th scope="row" colSpan={3} className={TH}>Subtotal</th>
        <td className={TD_MONEY} data-testid="ew-invoice-subtotal">{fmtMoney(inv.subtotal, cur)}</td>
      </tr>
      {taxRows.length > 0 ? (
        taxRows.map((r, i) => (
          <MoneyRow key={i} label={r.percent !== null ? `${r.label} (${fmtPct(r.percent)})` : r.label} value={fmtMoney(r.amount, cur)} testId="ew-invoice-tax" />
        ))
      ) : (
        <MoneyRow label={inv.taxExempt ? 'Tax (customer exempt)' : 'Tax'} value={fmtMoney(inv.taxAmount, cur)} testId="ew-invoice-tax" />
      )}
      {deposits.map((l) => (
        <MoneyRow key={l.id} label="Deposit credited" sub={l.description} value={fmtMoney(l.amount, cur)} testId="ew-invoice-deposit" />
      ))}
      <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
        <th scope="row" colSpan={3} className={TH}>Total ({cur || '—'})</th>
        <td className={`${TD_MONEY} text-base font-extrabold`} data-testid="ew-invoice-total">{fmtMoney(inv.total, cur)}</td>
      </tr>
      <MoneyRow label="Paid" value={fmtMoney(inv.amountPaid, cur)} testId="ew-invoice-paid" />
      <MoneyRow label="Balance due" value={fmtMoney(inv.balanceDue, cur)} testId="ew-invoice-balance" strong />
    </tfoot>
  );
}

export default function EwInvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(`${EW_FINANCE_INVOICES_PATH}?vertical=earthworks`);
  const { user } = useAuth();
  const tz = useTenantTimeZone();
  const today = useEwMeta().data?.meta.today ?? null;
  const query = useEwInvoice(id);
  const inv = query.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !inv && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const [panel, setPanel] = useState<'edit' | 'sent' | 'payment' | 'void' | null>(null);

  const canWrite = canWriteEwInvoices(user?.role);
  const status = inv?.status ?? null;
  const cur = inv?.currency ?? null;

  const actions = inv && canWrite ? (
    <div className="flex flex-wrap gap-2" data-testid="ew-invoice-actions">
      {ewInvoiceIsDraft(status) && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setPanel('edit')} data-testid="ew-invoice-edit">
          <Pencil className="size-4" aria-hidden="true" />
          Edit details
        </button>
      )}
      {ewInvoiceCanVoid(status) && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setPanel('void')} data-testid="ew-invoice-void">
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {ewInvoiceCanTakePayment(inv) && (
        <button type="button" className={EW_PRIMARY} onClick={() => setPanel('payment')} data-testid="ew-invoice-record-payment">
          <CreditCard className="size-4" aria-hidden="true" />
          Record payment
        </button>
      )}
      {/* Pay now (Batch 6b — a card link for the customer) goes here. No button until it ships. */}
      {ewInvoiceCanMarkSent(status) && (
        <button type="button" className={EW_PRIMARY} onClick={() => setPanel('sent')} data-testid="ew-invoice-mark-sent">
          <Send className="size-4" aria-hidden="true" />
          Mark sent
        </button>
      )}
    </div>
  ) : undefined;

  const groups = inv ? ewInvoiceLineGroups(inv) : [];

  return (
    <div data-testid="ew-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title={inv?.invoiceNumber || (query.isLoading ? 'Invoice' : '—')}
        description={inv ? `${inv.customerName ?? '—'} · ${ewInvoiceKindText(inv.kind, inv.kindLabel)}` : undefined}
        onBack={back}
        backLabel="Back to invoices"
        actions={actions}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="ew-invoice-detail">
          {!inv && query.isLoading ? (
            <EwLoading label="Loading the invoice" />
          ) : notFound ? (
            <EwPanel eyebrow="Invoice" title="Invoice">
              <EwEmpty>This invoice was not found.</EwEmpty>
            </EwPanel>
          ) : !inv ? (
            <EwPanel eyebrow="Invoice" title="Invoice">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this invoice" />
            </EwPanel>
          ) : (
            <>
              {status === 'void' && (
                <div role="status" data-testid="ew-invoice-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {ewInstant(inv.voidedAt, tz)}{inv.voidedByName ? ` by ${inv.voidedByName}` : ''}. This invoice is final.</p>
                  <p className="mt-1 whitespace-pre-wrap">{inv.voidReason || '—'}</p>
                </div>
              )}
              {status === 'paid' && (
                <p role="status" data-testid="ew-invoice-paid-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Paid in full {ewInstant(inv.paidAt, tz)}. This invoice is final.
                </p>
              )}
              {inv.overlapSummary && (
                <p role="note" data-testid="ew-invoice-overlap-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Overlap confirmed{inv.overlapConfirmedByName ? ` by ${inv.overlapConfirmedByName}` : ''}{inv.overlapConfirmedAt ? ` on ${ewInstant(inv.overlapConfirmedAt, tz)}` : ''}: {inv.overlapSummary}
                </p>
              )}
              {canWrite && panel === 'edit' && ewInvoiceIsDraft(status) && <EwInvoiceEditPanel inv={inv} onDone={() => setPanel(null)} />}
              {canWrite && panel === 'sent' && ewInvoiceCanMarkSent(status) && <EwInvoiceMarkSentPanel inv={inv} onDone={() => setPanel(null)} />}
              {canWrite && panel === 'payment' && ewInvoiceCanTakePayment(inv) && <EwInvoicePaymentPanel inv={inv} today={today} onDone={() => setPanel(null)} />}
              {canWrite && panel === 'void' && ewInvoiceCanVoid(status) && <EwInvoiceVoidPanel inv={inv} onDone={() => setPanel(null)} />}

              <EwPanel eyebrow="Invoice" title="Details" testId="ew-invoice-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <EwSpec label="Status" value={<span data-testid="ew-invoice-status"><EwBadge skin={ewInvoiceStatusSkin(status)} value={status} /></span>} />
                  <EwSpec label="Kind" value={<span data-testid="ew-invoice-kind">{ewInvoiceKindText(inv.kind, inv.kindLabel)}</span>} />
                  <EwSpec label="Invoice date" value={ewDay(inv.invoiceDate)} />
                  <EwSpec label="Due date" value={<span data-testid="ew-invoice-due">{ewDay(inv.dueDate)}</span>} />
                  <EwSpec label="Customer" value={inv.customerName || '—'} />
                  <EwSpec label="Job" value={inv.jobId ? <Link className="underline" to={`/earthworks/jobs/${inv.jobId}?tab=invoices`}>{inv.jobNumber || '—'}</Link> : '—'} mono />
                  <EwSpec label="Site" value={inv.siteName || '—'} />
                  <EwSpec label="Quote" value={inv.sourceQuoteId ? <Link className="underline" to={`/quotes/${inv.sourceQuoteId}`}>{inv.quoteNumber || '—'}</Link> : '—'} mono />
                  {inv.sourceChangeOrderId && (
                    <EwSpec label="Change order" value={<Link className="underline" to={`/earthworks/change-orders/${inv.sourceChangeOrderId}`}>{inv.changeOrderNumber || '—'}</Link>} mono />
                  )}
                  {inv.kind === 'progress_draw' || inv.kind === 'from_quote' ? (
                    <EwSpec label="Draw" value={`${inv.drawNumber !== null ? `#${fmtNumber(inv.drawNumber)} · ` : ''}${fmtPct(inv.quotePct)} of the quote`} />
                  ) : null}
                  <EwSpec label={`${inv.holdbackLabel || 'Holdback'} %`} value={<span data-testid="ew-invoice-holdback-pct">{fmtPct(inv.holdbackPct)}</span>} />
                  <EwSpec label="Province / state" value={inv.jurisdiction || '—'} />
                  <EwSpec label="Currency" value={cur || '—'} />
                  <EwSpec label="PO number" value={<span data-testid="ew-invoice-po">{inv.poNumber || '—'}</span>} mono />
                  <EwSpec label="AFE number" value={inv.afeNumber || '—'} mono />
                  <EwSpec label="Cost code" value={inv.costCode || '—'} mono />
                  <EwSpec label="Accounting email" value={inv.accountingEmail || '—'} />
                  <EwSpec label="Created" value={`${ewInstant(inv.createdAt, tz)}${inv.createdByName ? ` by ${inv.createdByName}` : ''}`} />
                  <EwSpec label="Sent" value={inv.sentAt ? `${ewInstant(inv.sentAt, tz)}${inv.sentByName ? ` by ${inv.sentByName}` : ''}` : 'Not sent'} />
                </dl>
                {inv.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{inv.notes}</p>}
              </EwPanel>

              <EwPanel eyebrow="Charges" title="Lines" testId="ew-invoice-lines">
                {groups.length === 0 ? (
                  <EwEmpty>This invoice has no lines.</EwEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      {groups.map((g) => (
                        <tbody key={g.key} data-testid="ew-invoice-line-group">
                          <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/30">
                            <th scope="colgroup" colSpan={4} className="px-4 py-2 text-left text-xs font-bold text-fieldiq-text">
                              <span className="font-mono">{g.title}</span>
                            </th>
                          </tr>
                          {g.lines.map((l) => (
                            <tr key={l.id} data-testid="ew-invoice-line" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                              <td className="px-4 py-3">
                                <p className="text-sm font-semibold text-white">{l.description || '—'}</p>
                                <p className="mt-0.5 text-xs text-fieldiq-muted">
                                  {[l.rateCode, l.workDate ? ewDay(l.workDate) : null, l.drawPct !== null ? `${fmtPct(l.drawPct)} draw` : null].filter(Boolean).join(' · ') || '—'}
                                </p>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="ew-invoice-line-qty">
                                {fmtNumber(l.quantity)}{l.quantity !== null && l.unit ? ` ${l.unit}` : ''}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="ew-invoice-line-rate">{fmtMoney(l.rate, cur)}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white" data-testid="ew-invoice-line-amount">{fmtMoney(l.amount, cur)}</td>
                            </tr>
                          ))}
                        </tbody>
                      ))}
                      <Totals inv={inv} />
                    </table>
                  </div>
                )}
              </EwPanel>

              {inv.tickets.length > 0 && (
                <EwPanel eyebrow="Billed" title="Field tickets" testId="ew-invoice-tickets">
                  <ul className="divide-y divide-fieldiq-border">
                    {inv.tickets.map((t) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="ew-invoice-ticket">
                        <Link to={`/earthworks/tickets/${t.id}`} className="font-mono font-extrabold text-white underline underline-offset-2">{t.ticketNumber}</Link>
                        <span className="text-fieldiq-text">{t.mode === 'per_day' ? ewDay(t.workDate) : 'Whole job'}</span>
                        <span className="font-mono text-fieldiq-text">{fmtMoney(t.subtotal, cur)} before tax</span>
                        {t.releasedAt && <span className="text-fieldiq-muted">Released {ewInstant(t.releasedAt, tz)}</span>}
                      </li>
                    ))}
                  </ul>
                </EwPanel>
              )}

              {ewInvoiceShowsPayLink(status) && <EwInvoicePayLinkPanel inv={inv} canWrite={canWrite} tz={tz} />}
              {ewInvoiceShowsQbo(status) && <EwInvoiceQboPanel inv={inv} canWrite={canWrite} tz={tz} />}

              <EwPanel eyebrow="Received" title="Payments" testId="ew-invoice-payments">
                {inv.payments.length === 0 ? (
                  <EwEmpty>No payments recorded on this invoice.</EwEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {inv.payments.map((p) => (
                      <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="ew-invoice-payment">
                        <span className="font-mono font-extrabold text-white">{fmtMoney(p.amount, cur)}</span>
                        <span className="text-fieldiq-text">{ewDay(p.paidOn)}</span>
                        <span className="text-fieldiq-text">{ewPaymentMethodText(p.method)}</span>
                        <span className="font-mono text-fieldiq-muted">Ref {p.reference || '—'}</span>
                        <span className="text-fieldiq-muted">Recorded by {p.recordedByName || '—'}</span>
                        {!p.counts && <span className="font-bold text-fi-signal-amber">Held for review: {p.reviewReason || '—'}. Not counted as paid.</span>}
                        {p.notes && <span className="w-full text-xs text-fieldiq-muted">{p.notes}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </EwPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
